import { useApolloClient } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { chunk, get, uniq } from 'lodash';
import { useCallback } from 'react';

import type { AdsPageV5, LineItemsPageV5, Query } from '../../../../apis/graphql';
import { TraffickingTableName } from '../../../../constants';
import { useFiltersContext } from '../../../../contexts/FilterContext';
import type { Nullable } from '../../../../models';
import { MAX_LIMIT } from '../../constants';
import { AD_PARENT_FRAGMENT, LINE_ITEM_PARENT_FRAGMENT } from './fragments';
import {
  GET_AD_PARENTS_BY_IDS,
  GET_ADS,
  GET_ADS_IDS,
  GET_LINE_ITEM_PARENTS_BY_IDS,
  GET_LINE_ITEMS_IDS,
  GET_LINEITEMS_BY_CAMPAIGNID_LIST,
} from './queries';
import type { QueryVariables } from './useTraffickerState';
import { DEFAULT_LIMIT } from './useTraffickerTable';

export type AssociatedEntities = Partial<Record<TraffickingTableName, string[]>>;

// unresolvedChildIds holds children this pass could not attribute to any parent.
export type ParentIdsOfChildren = { parentIds: Set<string>; unresolvedChildIds: string[] };

export type UseAssociatedEntities = {
  getAssociatedEntities: (tableName: TraffickingTableName, ids: string[]) => Promise<AssociatedEntities>;
  getDescendantEntities: (tableName: TraffickingTableName, ids: string[]) => Promise<AssociatedEntities>;
  getAncestorEntities: (tableName: TraffickingTableName, ids: string[]) => AssociatedEntities;
  getParentIdsOfChildren: (
    childTable: TraffickingTableName,
    childIds: string[],
    options?: { cacheOnly?: boolean }
  ) => Promise<ParentIdsOfChildren>;
};

type ParentRef = Nullable<{ id: Nullable<string> }>;

// Child ids per parent-lookup request, well under MAX_LIMIT.
const PARENT_LOOKUP_CHUNK_SIZE = 200;

type ParentLookupVariables = { idList: string[]; limit: number };

// Covers both queries: ads carry lineItem, line items carry campaign.
type ParentLookupNode = { id: string; lineItem?: ParentRef; campaign?: ParentRef };

type ParentLookupData = {
  lineItems?: { edges: { node: ParentLookupNode }[] };
  ads?: { edges: { node: ParentLookupNode }[] };
};

// Both parent fields are nullable, and a missing campaign id can arrive as '', so drop falsy ids.
const definedIds = (ids: (Nullable<string> | undefined)[]): string[] => uniq(ids.filter((id): id is string => !!id));

type AssociatedEntitiesQueryVariables = {
  lineItemQueryVariables: QueryVariables;
  adsQueryVariables: QueryVariables;
};

//Need this transformation since we have gql queries lineItemsV5 and adsV5, but in gql requests we fetch them as lineItems and ads
const transformDataType = (
  data: Pick<Query, 'lineItemsV5' | 'adsV5'>
): { lineItems: LineItemsPageV5; ads: AdsPageV5 } => {
  return (data as unknown) as { lineItems: LineItemsPageV5; ads: AdsPageV5 };
};

export const useAssociatedEntities = ({
  lineItemQueryVariables,
  adsQueryVariables,
}: AssociatedEntitiesQueryVariables): UseAssociatedEntities => {
  const { query: clientQuery, cache } = useApolloClient();
  const { filters } = useFiltersContext();

  const queryAssociatedItems = useCallback(
    async <T extends Partial<QueryVariables>>(
      query: DocumentNode,
      key: 'lineItems' | 'ads',
      queryVariables: Partial<T>
    ): Promise<string[]> => {
      const variables = { limit: MAX_LIMIT, ...queryVariables } as T;

      const { data } = await clientQuery<Pick<Query, 'lineItemsV5' | 'adsV5'>, T>({ query, variables });

      return transformDataType(data)[key].edges.map(({ node }) => node.id);
    },
    [clientQuery]
  );

  const readAssociatedItems = useCallback(
    async ({
      ids,
      query,
      variables,
      key,
      path,
    }: {
      ids: string[];
      query: DocumentNode;
      variables: QueryVariables;
      key: 'lineItems' | 'ads';
      path: string;
    }): Promise<string[]> => {
      const data = cache.readQuery<Pick<Query, 'lineItemsV5' | 'adsV5'>>({ query, variables });

      if (!data) return [];

      return transformDataType(data)[key].edges.reduce<string[]>((acc, { node }) => {
        const entityId = get(node, path);

        if (ids.includes(entityId)) return [...acc, node.id];

        return acc;
      }, []);
    },
    [cache]
  );

  const getAssociatedItems = useCallback(
    async <T>({
      query,
      ids,
      key,
      variables,
      cachedQuery,
      cachedVariables,
      path,
    }: {
      ids: string[];
      query: DocumentNode;
      variables: Partial<T>;
      cachedQuery: DocumentNode;
      cachedVariables: QueryVariables;
      key: 'lineItems' | 'ads';
      path: string;
    }): Promise<string[]> => {
      const selectedRowIds = filters[key]?.selectedRowIds;

      if (!selectedRowIds) return [];

      return selectedRowIds.length > DEFAULT_LIMIT
        ? queryAssociatedItems(query, key, variables)
        : readAssociatedItems({ query: cachedQuery, ids, variables: cachedVariables, key, path });
    },
    [filters, queryAssociatedItems, readAssociatedItems]
  );

  const getLineItemsAssociatedEntities = useCallback(
    async (ids: string[]): Promise<Partial<Record<TraffickingTableName, string[]>>> => {
      const adsIds = await getAssociatedItems({
        query: GET_ADS_IDS,
        ids,
        key: 'ads',
        variables: {
          lineItemsIds: ids,
        },
        cachedQuery: GET_ADS,
        cachedVariables: adsQueryVariables,
        path: 'lineItem.id',
      });

      return {
        ads: adsIds,
      };
    },
    [adsQueryVariables, getAssociatedItems]
  );

  const getCampaignsAssociatedEntities = useCallback(
    async (ids: string[]): Promise<Partial<Record<TraffickingTableName, string[]>>> => {
      const lineItemsIds = await getAssociatedItems({
        query: GET_LINE_ITEMS_IDS,
        ids,
        key: 'lineItems',
        variables: { ids },
        cachedQuery: GET_LINEITEMS_BY_CAMPAIGNID_LIST,
        cachedVariables: lineItemQueryVariables,
        path: 'campaign.id',
      });

      const adsIds = await getAssociatedItems({
        query: GET_ADS_IDS,
        ids,
        key: 'ads',
        variables: {
          campaignsIds: ids,
        },
        cachedQuery: GET_ADS,
        cachedVariables: adsQueryVariables,
        path: 'lineItem.campaign.id',
      });

      return {
        lineItems: lineItemsIds,
        ads: adsIds,
      };
    },
    [adsQueryVariables, getAssociatedItems, lineItemQueryVariables]
  );

  const getAssociatedEntities = useCallback(
    (tableName: TraffickingTableName, ids: string[]): Promise<Partial<Record<TraffickingTableName, string[]>>> => {
      if (tableName === TraffickingTableName.campaigns) return getCampaignsAssociatedEntities(ids);
      if (tableName === TraffickingTableName.lineItems) return getLineItemsAssociatedEntities(ids);

      return {} as Promise<Record<TraffickingTableName, string[]>>;
    },
    [getCampaignsAssociatedEntities, getLineItemsAssociatedEntities]
  );

  /**
   * Every descendant of the given ids, always fetched from the server at MAX_LIMIT.
   *
   * Deliberately bypasses getAssociatedItems, which is built for the intersection and
   * deselection paths: it returns [] when the target tab has no selection yet, and
   * otherwise reads the Apollo cache — so it only ever yields rows already paged in.
   * Cascading selection needs the complete child set regardless of what is selected
   * or loaded, so it goes straight to queryAssociatedItems.
   */
  const getDescendantEntities = useCallback(
    async (tableName: TraffickingTableName, ids: string[]): Promise<AssociatedEntities> => {
      if (!ids.length) return {};

      if (tableName === TraffickingTableName.campaigns) {
        const [lineItems, ads] = await Promise.all([
          queryAssociatedItems(GET_LINE_ITEMS_IDS, 'lineItems', { ids }),
          queryAssociatedItems(GET_ADS_IDS, 'ads', { campaignsIds: ids }),
        ]);

        return { lineItems, ads };
      }

      if (tableName === TraffickingTableName.lineItems) {
        return { ads: await queryAssociatedItems(GET_ADS_IDS, 'ads', { lineItemsIds: ids }) };
      }

      return {};
    },
    [queryAssociatedItems]
  );

  /** The cached parent id of one row, or null when that row's parent was never fetched. */
  const readParentId = useCallback(
    (typename: 'AdV5' | 'LineItemV5', id: string): Nullable<string> => {
      const fragment = typename === 'AdV5' ? AD_PARENT_FRAGMENT : LINE_ITEM_PARENT_FRAGMENT;
      const parentRef = cache.readFragment<{ lineItem?: ParentRef; campaign?: ParentRef }>({
        id: cache.identify({ __typename: typename, id }),
        fragment,
      });

      return (typename === 'AdV5' ? parentRef?.lineItem?.id : parentRef?.campaign?.id) ?? null;
    },
    [cache]
  );

  /**
   * Every ancestor of the given ids, read from the normalized Apollo cache — no network call, and
   * independent of which rows either tab has loaded. Ads chain two levels: ad to line item, line
   * item to campaign. An id whose parent is not cached is absent from the result, leaving that
   * ancestor untouched rather than guessed at.
   */
  const getAncestorEntities = useCallback(
    (tableName: TraffickingTableName, ids: string[]): AssociatedEntities => {
      if (!ids.length || tableName === TraffickingTableName.campaigns) return {};

      if (tableName === TraffickingTableName.lineItems) {
        return { campaigns: definedIds(ids.map((id) => readParentId('LineItemV5', id))) };
      }

      const lineItems = definedIds(ids.map((id) => readParentId('AdV5', id)));

      return { lineItems, campaigns: definedIds(lineItems.map((id) => readParentId('LineItemV5', id))) };
    },
    [readParentId]
  );

  // Queries parent ids for the given children, one request per chunk. Only a parent id resolves a
  // child, so one that does not come back, or comes back with a null parent, is unresolved.
  const queryParentIdsByChildIds = useCallback(
    async (
      childTable: TraffickingTableName.lineItems | TraffickingTableName.ads,
      childIds: string[]
    ): Promise<{ parentIds: string[]; unresolvedChildIds: string[] }> => {
      const isAds = childTable === TraffickingTableName.ads;
      const query = isAds ? GET_AD_PARENTS_BY_IDS : GET_LINE_ITEM_PARENTS_BY_IDS;

      const results = await Promise.all(
        chunk(childIds, PARENT_LOOKUP_CHUNK_SIZE).map((idList) =>
          clientQuery<ParentLookupData, ParentLookupVariables>({ query, variables: { idList, limit: idList.length } })
        )
      );

      const parentIds: string[] = [];
      const resolvedChildIds = new Set<string>();

      results.forEach(({ data }) => {
        (isAds ? data?.ads : data?.lineItems)?.edges.forEach(({ node }) => {
          const parentId = isAds ? node.lineItem?.id : node.campaign?.id;

          if (!parentId) return;

          resolvedChildIds.add(node.id);
          parentIds.push(parentId);
        });
      });

      return { parentIds, unresolvedChildIds: childIds.filter((id) => !resolvedChildIds.has(id)) };
    },
    [clientQuery]
  );

  // Parent ids of the given children, read from the cache first and queried only for the misses.
  // cacheOnly stops before the query and reports the misses as unresolved.
  const getParentIdsOfChildren = useCallback(
    async (
      childTable: TraffickingTableName,
      childIds: string[],
      { cacheOnly = false }: { cacheOnly?: boolean } = {}
    ): Promise<ParentIdsOfChildren> => {
      if (!childIds.length || childTable === TraffickingTableName.campaigns) {
        return { parentIds: new Set<string>(), unresolvedChildIds: [] };
      }

      const isAds = childTable === TraffickingTableName.ads;
      const parentIds = new Set<string>();
      const cacheMisses: string[] = [];

      childIds.forEach((childId) => {
        const parentId = readParentId(isAds ? 'AdV5' : 'LineItemV5', childId);

        if (parentId) {
          parentIds.add(parentId);
        } else {
          cacheMisses.push(childId);
        }
      });

      if (cacheOnly || !cacheMisses.length) return { parentIds, unresolvedChildIds: cacheMisses };

      const fetched = await queryParentIdsByChildIds(
        isAds ? TraffickingTableName.ads : TraffickingTableName.lineItems,
        cacheMisses
      );

      fetched.parentIds.forEach((parentId) => parentIds.add(parentId));

      return { parentIds, unresolvedChildIds: fetched.unresolvedChildIds };
    },
    [queryParentIdsByChildIds, readParentId]
  );

  return {
    getAssociatedEntities,
    getDescendantEntities,
    getAncestorEntities,
    getParentIdsOfChildren,
  };
};
